import { useEffect, useState } from "react";
import { BookOpen, ChevronDown } from "lucide-react";
import { adminApi } from "@/lib/api";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

const Rule = ({ title, children, testid }) => (
  <div data-testid={testid} className="space-y-1">
    <div className="text-[11px] tracking-[0.25em] uppercase font-bold text-[var(--trio-primary)]">{title}</div>
    <ul className="list-disc pl-5 space-y-1 text-sm text-[var(--trio-text)]">{children}</ul>
  </div>
);

const ticketRate = (e) =>
  e.commission_type === "percent"
    ? `${e.commission_value}% of the ${money(e.ticket_price)} ticket = ${money(e.per_ticket)} per ticket`
    : `${money(e.per_ticket)} per ticket`;

export const HowItWorks = ({ topics = ["link", "money", "leaderboard", "contests", "reviews"], defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const [rules, setRules] = useState(null);

  useEffect(() => {
    adminApi.get("/staff/rules").then(({ data }) => setRules(data)).catch(() => setRules({ reservation_commission: 0, ticket_events: [] }));
  }, []);

  const show = (t) => topics.includes(t);

  return (
    <div data-testid="how-it-works" className="border-2 border-[var(--trio-text)] bg-[var(--trio-surface)]">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="how-it-works-toggle"
        className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left"
      >
        <span className="flex items-center gap-2 font-display uppercase text-xl tracking-tight">
          <BookOpen className="w-5 h-5 text-[var(--trio-primary)]" /> How It Works · The Rules
        </span>
        <ChevronDown className={`w-5 h-5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && rules && (
        <div className="px-5 pb-6 space-y-5 border-t border-[var(--trio-border)] pt-5">
          {show("link") && (
            <Rule title="1 · Your Link & QR" testid="rule-link">
              <li>Share your personal QR or link (in <strong>My Link</strong>). When a guest opens it, you get the credit for whatever they book.</li>
              <li>The credit stays on their phone for <strong>7 days</strong>, so it still counts if they book later.</li>
              <li>If they open another crew member's link after yours, the <strong>most recent link</strong> gets the credit.</li>
            </Rule>
          )}
          {show("money") && (
            <Rule title="2 · How You Get Paid (Commissions)" testid="rule-money">
              <li>
                <strong>RSVPs / reservations:</strong> {rules.reservation_commission > 0 ? <><strong>{money(rules.reservation_commission)}</strong> for each booking from your link</> : "commission per booking is set by the owner"}, once it's <strong>confirmed or scanned in at the door</strong>. Cancelled bookings pay $0.
              </li>
              <li>
                <strong>Tickets:</strong> you earn on every <strong>paid</strong> ticket bought through your link. Refunded tickets pay $0.
                {rules.ticket_events.length > 0 && (
                  <ul className="list-[circle] pl-5 mt-1 space-y-0.5" data-testid="rule-ticket-rates">
                    {rules.ticket_events.map((e) => (
                      <li key={e.title + e.event_date}><strong>{e.title}</strong> ({e.event_date}): {ticketRate(e)}</li>
                    ))}
                  </ul>
                )}
              </li>
              <li>Your money shows as <strong>Earned</strong> first. It turns <strong>Paid</strong> once the owner pays you out.</li>
            </Rule>
          )}
          {show("leaderboard") && (
            <Rule title="3 · Leaderboard" testid="rule-leaderboard">
              <li>Ranked by <strong>commission dollars earned</strong> (confirmed RSVPs + paid tickets). Ties go to whoever has more bookings.</li>
              <li>The monthly board <strong>resets on the 1st</strong>. The #1 earner goes into the <strong>Hall of Fame</strong> and the whole crew gets an email.</li>
            </Rule>
          )}
          {show("contests") && (
            <Rule title="4 · Contests (Points)" testid="rule-contests">
              <li><strong>1 point</strong> for each RSVP from your link that <strong>actually shows up and gets scanned</strong> at the door.</li>
              <li><strong>1 point</strong> for each ticket from your link that's <strong>scanned at the door</strong> (a 4-ticket order scanned in = 4 points).</li>
              <li><strong>1 point</strong> for each <strong>5-star review</strong> a guest leaves you.</li>
              <li>Only counts between the contest's start and end dates (Huntsville time). No scan = no point, so make sure your people show their QR at the door.</li>
              <li>Ties go to whoever has more scanned tickets. The owner hands out the prize after the contest ends.</li>
            </Rule>
          )}
          {show("reviews") && (
            <Rule title="5 · Reviews" testid="rule-reviews">
              <li>Show guests your <strong>Rate Me</strong> QR (in <strong>My Reviews</strong>). They tap 1–5 stars and can leave a comment.</li>
              <li>You see your own reviews and average. The owner sees everyone's.</li>
              <li>Reviewing yourself or faking reviews gets them deleted and removes your points.</li>
            </Rule>
          )}
        </div>
      )}
    </div>
  );
};
